import { useReactiveVar } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import type React from 'react';
import Logo from 'src/components/logo';
import { HOME_PATH } from 'src/constants';
import { useFilterPopover } from 'src/context/FilterPopoverContext';
import SearchAppBar from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopFilterNavBar/SearchAppBar/SearchAppBar';
import NavBarUserName from 'src/HomePage/TopNavBar/NavBarUserName/NavBarUserName';
import styles from 'src/HomePage/TopNavBar/TopNavBarStyles';
import { useInitialNavParams } from 'src/helpers/filters';
import viewStateVar from 'src/store/viewState';
import NavLogo from './AkomiLogo/AkomiNavLogo';

interface BaseNavBarProps {
  children: React.ReactNode;
  className?: string;
}

export const BaseNavBar: React.FC<BaseNavBarProps> = ({ children, className = '' }) => {
  const classes = styles();
  return (
    <div className={`${classes.topNavBar} ${className}`}>
      <div className={classes.topNavBarItems}>{children}</div>
    </div>
  );
};

interface TopNavBarProps {
  simple?: boolean;
  showSearch?: boolean;
  currentPage?: string;
}

const TopNavBar: React.FC<TopNavBarProps> = ({ simple, showSearch, currentPage }) => {
  const navigate = useNavigate();
  const buildNavParams = useInitialNavParams();
  const homeNavParams = buildNavParams(HOME_PATH);
  const { filterChips } = useReactiveVar(viewStateVar);
  const { enabled: filterEnabled, open: openFilter } = useFilterPopover();
  const hasChips = (filterChips as unknown[]).length > 0;

  const goHome = (): void => {
    navigate({ to: homeNavParams.to, search: homeNavParams.search });
  };

  if (simple) {
    return (
      <BaseNavBar>
        <button
          type="button"
          onClick={goHome}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <NavLogo />
        </button>
      </BaseNavBar>
    );
  }

  if (showSearch) {
    return (
      <BaseNavBar>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            onClick={goHome}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0 }}
          >
            <Logo height="24" style={{ maxWidth: 100, cursor: 'pointer' }} />
          </button>
        </div>
        <div style={{ flex: '0 1 50%', minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <SearchAppBar />
          {filterEnabled && !hasChips && (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => openFilter(e.currentTarget)}
              sx={{
                textTransform: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                borderRadius: '12px',
                fontSize: '0.75rem',
                height: 26,
                minWidth: 0,
                padding: '0 10px',
                '& .MuiButton-startIcon': { marginRight: '4px' },
                '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: '14px' },
              }}
            >
              Filter
            </Button>
          )}
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <NavBarUserName currentPage={currentPage} />
        </div>
      </BaseNavBar>
    );
  }

  return (
    <BaseNavBar>
      <button
        type="button"
        onClick={goHome}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <NavLogo />
      </button>
      <NavBarUserName currentPage={currentPage} />
    </BaseNavBar>
  );
};

export default TopNavBar;
